import type { Setter } from 'jotai';
import noop from 'lodash/noop';
import type { DynamicAnswerSpanT } from '../../../studio-store/types';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
type DynamicAnswers = DynamicAnswerSpanT[] | undefined;
function writeDynamicAnswers(set: Setter, annotationId: string, dynamicAnswers: DynamicAnswers): void {
  set(dispatchStudioStoreWriteAtom, {
    type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
    annotationId,
    partialPayload: {
      dynamicAnswers
    }
  });
}

/**
 * Applies a reversible edit to an annotation's dynamic answers.
 *
 * @param previous - The annotation's current dynamic answers, captured before the edit. Used both
 *   as the input to `transform` and as the value restored by the returned undo callback.
 * @param transform - Produces the revised segments from `previous` (empty array when there were
 *   none yet). Returns `undefined` to signal "no change needed", in which case nothing is written
 *   and undo is a no-op.
 * @returns An undo callback that rewrites `previous` back into the store. It is `noop` when no write
 *   occurred (`transform` returned `undefined`).
 */
export function reviseDynamicAnswers(set: Setter, annotationId: string, previous: DynamicAnswers, transform: (current: DynamicAnswerSpanT[]) => DynamicAnswerSpanT[] | undefined): () => void {
  const next = transform(previous ?? []);
  if (next === undefined || !previous?.length && !next.length) {
    return noop;
  }
  // Overwrite the store with the revised segments, and hand back a callback that overwrites it
  // with the original segments (or clears it, if there were none) to undo this edit.
  writeDynamicAnswers(set, annotationId, next);
  return () => writeDynamicAnswers(set, annotationId, previous);
}